import React, { useState, useEffect, useCallback } from 'react';
import { 
  powerLocationAPI,
  inverterConfigAPI,
  panelConfigAPI,
  temperatureConfigAPI,
  showApiError 
} from '../../services/api';
import '../Registration/PowerPlantInquiry.css';
import '../Registration/RegistrationComponents.css';
import './PowerPlantSettings.css';

const PowerPlantSettings = ({ powerPlant, user, onDelete }) => {
  const isAdmin = user?.role === 'ADMIN';
  
  const [powerPlantInfo, setPowerPlantInfo] = useState(null);
  const [inverters, setInverters] = useState([]);
  const [panels, setPanels] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  
  // 수정 모드
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPowerLocation, setEditedPowerLocation] = useState(null);
  
  // 장비 수정 모드
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDeletePowerPlantConfirm, setShowDeletePowerPlantConfirm] = useState(false);

  // 발전소 정보 로드
  const loadPowerPlantInfo = useCallback(async (powerLocationId) => {
    try {
      const data = await powerLocationAPI.get(powerLocationId);
      setPowerPlantInfo(data);
      setEditedPowerLocation({...data});
    } catch (error) {
      console.error('발전소 정보 로드 실패:', error);
      showApiError(error);
    }
  }, []);

  // 장비 정보 로드
  const loadEquipmentInfo = async (powerLocationId) => {
    setLoadingEquipment(true);
    try {
      const [inverterList, panelList, sensorList] = await Promise.all([
        inverterConfigAPI.getInverterList(powerLocationId).catch(() => []),
        panelConfigAPI.getPanelList(powerLocationId).catch(() => []),
        temperatureConfigAPI.getTemperatureList(powerLocationId).catch(() => [])
      ]);
      
      setInverters(inverterList || []);
      setPanels(panelList || []);
      setSensors(sensorList || []);
    } catch (error) {
      console.error('장비 정보 로드 실패:', error);
      showApiError(error);
    } finally {
      setLoadingEquipment(false);
    }
  };

  useEffect(() => {
    if (powerPlant?.id) {
      setLoading(true);
      Promise.all([
        loadPowerPlantInfo(powerPlant.id),
        loadEquipmentInfo(powerPlant.id)
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [powerPlant?.id, loadPowerPlantInfo]);

  // 수정 모드 토글
  const handleEditToggle = () => {
    if (isEditMode) {
      // 수정 취소
      setEditedPowerLocation({...powerPlantInfo});
    }
    setIsEditMode(!isEditMode);
  };

  // 발전소 정보 수정 저장
  const handleSavePowerLocation = async () => {
    if (!editedPowerLocation || !powerPlantInfo) return;

    try {
      await powerLocationAPI.update(powerPlantInfo.id, editedPowerLocation);
      alert('발전소 정보가 수정되었습니다.');
      await loadPowerPlantInfo(powerPlantInfo.id);
      setIsEditMode(false);
    } catch (error) {
      console.error('발전소 정보 수정 실패:', error);
      showApiError(error);
    }
  };

  // 발전소 삭제 실행
  const handleDeletePowerPlant = async () => {
    if (!powerPlantInfo) return;

    try {
      await powerLocationAPI.delete(powerPlantInfo.id);
      alert('발전소가 삭제되었습니다.');
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('발전소 삭제 실패:', error);
      showApiError(error);
    } finally {
      setShowDeletePowerPlantConfirm(false);
    }
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (field, value) => {
    setEditedPowerLocation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 장비 수정 시작
  const handleEditEquipment = (type, equipment) => {
    setEditingEquipment({ type, id: equipment.id, data: {...equipment} });
  };

  // 장비 수정 취소
  const handleCancelEditEquipment = () => {
    setEditingEquipment(null);
  };

  // 장비 수정 저장
  const handleSaveEquipment = async () => {
    if (!editingEquipment || !powerPlantInfo) return;

    try {
      const { type, id, data } = editingEquipment;
      
      if (type === 'inverter') {
        const updateData = {
          inverterDeviceName: data.inverterDeviceName,
          inverterId: data.inverterId,
          name: data.name,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout,
          capacity: data.capacity
        };
        await inverterConfigAPI.update(id, updateData);
        alert('인버터 정보가 수정되었습니다.');
      } else if (type === 'sensor') {
        const updateData = {
          name: data.name,
          sensorId: data.sensorId,
          protocolType: data.protocolType,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout
        };
        await temperatureConfigAPI.update(id, updateData);
        alert('온도 센서 정보가 수정되었습니다.');
      } else if (type === 'panel') {
        const updateData = {
          name: data.name,
          panelId: data.panelId,
          channel: data.channel,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout
        };
        await panelConfigAPI.update(id, updateData);
        alert('접속반 정보가 수정되었습니다.');
      }

      // 장비 정보 다시 로드
      await loadEquipmentInfo(powerPlantInfo.id);
      setEditingEquipment(null);
    } catch (error) {
      console.error('장비 정보 수정 실패:', error);
      showApiError(error);
    }
  };

  // 장비 삭제 확인
  const handleDeleteEquipmentConfirm = (type, id, name) => {
    setShowDeleteConfirm({ type, id, name });
  };

  // 장비 삭제 실행
  const handleDeleteEquipment = async () => {
    if (!showDeleteConfirm || !powerPlantInfo) return;

    try {
      const { type, id } = showDeleteConfirm;
      
      if (type === 'inverter') {
        await inverterConfigAPI.delete(id);
        alert('인버터가 삭제되었습니다.');
      } else if (type === 'sensor') {
        await temperatureConfigAPI.delete(id);
        alert('온도 센서가 삭제되었습니다.');
      } else if (type === 'panel') {
        await panelConfigAPI.delete(id);
        alert('접속반이 삭제되었습니다.');
      }

      // 장비 정보 다시 로드
      await loadEquipmentInfo(powerPlantInfo.id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('장비 삭제 실패:', error);
      showApiError(error);
    }
  };

  // 장비 필드 변경
  const handleEquipmentFieldChange = (field, value) => {
    setEditingEquipment(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="power-plant-settings">
        <div className="inquiry-loading-equipment">
          <div className="loading-spinner"></div>
          <p>정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="power-plant-settings">
      <div className="inquiry-modal-body">
        <div className="inquiry-info">
          {/* 발전소 정보 그룹 */}
          <div className="inquiry-info-section">
            <div className="inquiry-section-header">
              <h4>발전소 정보</h4>
              {isAdmin && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    className={`btn btn-sm ${isEditMode ? 'btn-secondary' : 'btn-warning'}`}
                    onClick={handleEditToggle}
                  >
                    {isEditMode ? '취소' : '수정'}
                  </button>
                </div>
              )}
            </div>
            <div className="inquiry-info-grid">
              <div className="inquiry-info-item">
                <label>시리얼 코드:</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    value={editedPowerLocation?.serialCode || ''}
                    onChange={(e) => handleInputChange('serialCode', e.target.value)}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.serialCode || '-'}</span>
                )}
              </div>
              <div className="inquiry-info-item">
                <label>발전소 명:</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    value={editedPowerLocation?.locationName || ''}
                    onChange={(e) => handleInputChange('locationName', e.target.value)}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.locationName || '-'}</span>
                )}
              </div>
              <div className="inquiry-info-item">
                <label>설비 용량 (kW):</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="number"
                    value={editedPowerLocation?.capacity || ''}
                    onChange={(e) => handleInputChange('capacity', parseFloat(e.target.value))}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.capacity ? `${powerPlantInfo.capacity}kW` : '-'}</span>
                )}
              </div>
              <div className="inquiry-info-item full-width">
                <label>주소:</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    value={editedPowerLocation?.location || ''}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.location || '-'}</span>
                )}
              </div>
              <div className="inquiry-info-item">
                <label>위도:</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="number"
                    step="0.000001"
                    value={editedPowerLocation?.latitude || ''}
                    onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.latitude || '-'}</span>
                )}
              </div>
              <div className="inquiry-info-item">
                <label>경도:</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="number"
                    step="0.000001"
                    value={editedPowerLocation?.longitude || ''}
                    onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
                    className="inquiry-input"
                  />
                ) : (
                  <span>{powerPlantInfo?.longitude || '-'}</span>
                )}
              </div>
            </div>
            {isEditMode && isAdmin && (
              <div className="inquiry-edit-actions">
                <button 
                  className="btn btn-success"
                  onClick={handleSavePowerLocation}
                >
                  저장
                </button>
              </div>
            )}
          </div>

          {/* 인버터 정보 */}
          {loadingEquipment ? (
            <div className="inquiry-loading-equipment">장비 정보 로딩 중...</div>
          ) : (
            <>
              {inverters.length > 0 && (
                <div className="inquiry-info-section">
                  <h4>인버터 목록 ({inverters.length}개)</h4>
                  <div className="inquiry-equipment-list">
                    {inverters.map((inverter) => {
                      const isEditing = editingEquipment?.type === 'inverter' && editingEquipment?.id === inverter.id;
                      const editData = isEditing ? editingEquipment.data : inverter;
                      
                      return (
                        <div key={inverter.id} className="inquiry-equipment-item">
                          <div className="inquiry-equipment-header">
                            <strong>{isEditing && isAdmin ? (
                              <input
                                type="text"
                                value={editData.name || ''}
                                onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                className="inquiry-input-inline"
                              />
                            ) : (inverter.name || '인버터')}</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <span className="inquiry-equipment-type">인버터</span>
                              {!isEditing && isAdmin && (
                                <>
                                  <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('inverter', inverter)}>수정</button>
                                  <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('inverter', inverter.id, inverter.name)}>삭제</button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="inquiry-equipment-details">
                            <div className="inquiry-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{inverter.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="inquiry-detail-row">
                              <span>장치명:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="text"
                                  value={editData.inverterDeviceName || ''}
                                  onChange={(e) => handleEquipmentFieldChange('inverterDeviceName', e.target.value)}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.inverterDeviceName || '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>인버터 ID:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.inverterId ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('inverterId', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.inverterId ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>포트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="text"
                                  value={editData.port || ''}
                                  onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.port || '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>보드레이트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.baudrate ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.baudrate ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>바이트 크기:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.bytesize ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.bytesize ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>타임아웃:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editData.timeout ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.timeout ?? '-'}s</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>용량:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editData.capacity ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('capacity', parseFloat(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{inverter.capacity ?? '-'}kW</span>
                              )}
                            </div>
                          </div>
                          {isEditing && isAdmin && (
                            <div className="inquiry-equipment-actions">
                              <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                              <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 온도 센서 정보 */}
              {sensors.length > 0 && (
                <div className="inquiry-info-section">
                  <h4>온도 센서 목록 ({sensors.length}개)</h4>
                  <div className="inquiry-equipment-list">
                    {sensors.map((sensor) => {
                      const isEditing = editingEquipment?.type === 'sensor' && editingEquipment?.id === sensor.id;
                      const editData = isEditing ? editingEquipment.data : sensor;
                      
                      return (
                        <div key={sensor.id} className="inquiry-equipment-item">
                          <div className="inquiry-equipment-header">
                            <strong>{isEditing && isAdmin ? (
                              <input
                                type="text"
                                value={editData.name || ''}
                                onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                className="inquiry-input-inline"
                              />
                            ) : (sensor.name || '온도센서')}</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <span className="inquiry-equipment-type">온도센서</span>
                              {!isEditing && isAdmin && (
                                <>
                                  <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('sensor', sensor)}>수정</button>
                                  <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('sensor', sensor.id, sensor.name)}>삭제</button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="inquiry-equipment-details">
                            <div className="inquiry-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{sensor.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="inquiry-detail-row">
                              <span>센서 ID:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.sensorId ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('sensorId', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.sensorId ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>프로토콜:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="text"
                                  value={editData.protocolType || ''}
                                  onChange={(e) => handleEquipmentFieldChange('protocolType', e.target.value)}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.protocolType || '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>포트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="text"
                                  value={editData.port || ''}
                                  onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.port || '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>보드레이트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.baudrate ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.baudrate ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>바이트 크기:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.bytesize ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.bytesize ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>타임아웃:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editData.timeout ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{sensor.timeout ?? '-'}s</span>
                              )}
                            </div>
                          </div>
                          {isEditing && isAdmin && (
                            <div className="inquiry-equipment-actions">
                              <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                              <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 접속반 정보 */}
              {panels.length > 0 && (
                <div className="inquiry-info-section">
                  <h4>접속반 목록 ({panels.length}개)</h4>
                  <div className="inquiry-equipment-list">
                    {panels.map((panel) => {
                      const isEditing = editingEquipment?.type === 'panel' && editingEquipment?.id === panel.id;
                      const editData = isEditing ? editingEquipment.data : panel;
                      
                      return (
                        <div key={panel.id} className="inquiry-equipment-item">
                          <div className="inquiry-equipment-header">
                            <strong>{isEditing && isAdmin ? (
                              <input
                                type="text"
                                value={editData.name || ''}
                                onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                className="inquiry-input-inline"
                              />
                            ) : (panel.name || '접속반')}</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <span className="inquiry-equipment-type">접속반</span>
                              {!isEditing && isAdmin && (
                                <>
                                  <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('panel', panel)}>수정</button>
                                  <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('panel', panel.id, panel.name)}>삭제</button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="inquiry-equipment-details">
                            <div className="inquiry-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{panel.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="inquiry-detail-row">
                              <span>판넬 ID:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.panelId ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('panelId', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.panelId ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>채널:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.channel ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('channel', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.channel ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>포트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="text"
                                  value={editData.port || ''}
                                  onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.port || '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>보드레이트:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.baudrate ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.baudrate ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>바이트 크기:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  value={editData.bytesize ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.bytesize ?? '-'}</span>
                              )}
                            </div>
                            <div className="inquiry-detail-row">
                              <span>타임아웃:</span>
                              {isEditing && isAdmin ? (
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editData.timeout ?? ''}
                                  onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                  className="inquiry-input-inline"
                                />
                              ) : (
                                <span>{panel.timeout ?? '-'}s</span>
                              )}
                            </div>
                          </div>
                          {isEditing && isAdmin && (
                            <div className="inquiry-equipment-actions">
                              <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                              <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 삭제 확인 모달 - 장비 */}
      {showDeleteConfirm && (
        <div className="inquiry-modal-overlay">
          <div className="inquiry-modal-content" style={{ maxWidth: '500px' }}>
            <div className="inquiry-modal-header">
              <h3>장비 삭제 확인</h3>
              <button className="inquiry-modal-close" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>
            <div className="inquiry-modal-body">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                  <strong>정말로 이 장비를 삭제하시겠습니까?</strong>
                </p>
                <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                  <p><strong>장비 유형:</strong> {showDeleteConfirm.type === 'inverter' ? '인버터' : showDeleteConfirm.type === 'sensor' ? '온도센서' : '접속반'}</p>
                  <p><strong>장비명:</strong> {showDeleteConfirm.name || '-'}</p>
                </div>
                <p style={{ color: '#dc3545', fontSize: '0.9rem' }}>이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="inquiry-modal-footer">
              <div className="inquiry-modal-footer-buttons">
                <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>취소</button>
                <button className="btn btn-danger" onClick={handleDeleteEquipment}>삭제</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 - 발전소 */}
      {showDeletePowerPlantConfirm && (
        <div className="inquiry-modal-overlay">
          <div className="inquiry-modal-content" style={{ maxWidth: '500px' }}>
            <div className="inquiry-modal-header">
              <h3>발전소 삭제 확인</h3>
              <button className="inquiry-modal-close" onClick={() => setShowDeletePowerPlantConfirm(false)}>×</button>
            </div>
            <div className="inquiry-modal-body">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                  <strong>정말로 발전소를 삭제하시겠습니까?</strong>
                </p>
                <p style={{ color: '#dc3545', fontSize: '0.9rem' }}>이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="inquiry-modal-footer">
              <div className="inquiry-modal-footer-buttons">
                <button className="btn btn-secondary" onClick={() => setShowDeletePowerPlantConfirm(false)}>취소</button>
                <button className="btn btn-danger" onClick={handleDeletePowerPlant}>삭제</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerPlantSettings;

